"use client"
import { TextField, TextFieldProps, useTheme } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { CalendarIcon } from "@tc/ui-dfe/icons"
import { toDayJs } from "@tc/ui-dfe/utils/convertor"
import { Dayjs } from "@tc/ui-dfe/utils/libs/dayjs"
import { nzLocaleText } from "@tc/ui-dfe/utils/libs/dayjs"
import get from "lodash/get"
import { FieldValues, UseControllerProps, useController } from "react-hook-form"
import { FormErrorPrompt } from "../FormErrorPrompt"
import type { FormInputBaseProps } from "../FormInputBase"
import { FormInputBase } from "../FormInputBase"

export type FormDatePickerProps<T extends FieldValues = FieldValues> =
  DatePickerProps<Dayjs> &
    TextFieldProps &
    UseControllerProps<T> & {
      isReadOnly?: boolean
    }

export const FormDatePicker = <T extends FieldValues>(
  props: FormDatePickerProps<T>,
) => {
  const { name, control, isReadOnly = false, disabled } = props
  const {
    field,
    formState: { errors },
  } = useController({ name, control })
  const error = get(errors, name)
  const theme = useTheme()
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="en-nz"
      localeText={nzLocaleText}
    >
      <Stack data-testid="test-formDatePicker">
        <DatePicker
          desktopModeMediaQuery={theme.breakpoints.up("sm")}
          {...field}
          disabled={disabled}
          value={field.value === undefined ? null : toDayJs(field.value)}
          slots={{
            textField: FormInputBase as typeof TextField,
            openPickerIcon: CalendarIcon,
          }}
          slotProps={{
            textField: {
              placeholder: "dd/mmm/yyyy",
              error: !!error?.message,
              isReadOnly,
              FormHelperTextProps: {
                component: "div",
                sx: {
                  ml: 0,
                },
              },
              helperText: (
                <FormErrorPrompt message={error?.message as string} />
              ),
            } as FormInputBaseProps,
          }}
        />
      </Stack>
    </LocalizationProvider>
  )
}
